import React, { useState, useEffect, PropsWithChildren } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import {
    Button,
    Group,
    Heading,
    InformationType,
    Level,
    Text,
    TextInput,
} from '@defa/defa-component-library';
import { createTokenMutation } from '@generated/admin-prime/@tanstack/react-query.gen';
import { BrandPanel } from '@fractions/brand-panel';
import { AuthLayout } from '@fractions/auth-layout';
import i18n from '@i18n';

// Seconds the "Try again" button stays disabled after a submit. This is UX only
// — it discourages accidental/impatient re-clicks on the same address; the real
// protection against resend abuse is server-side rate limiting.
const RESEND_COOLDOWN_SECONDS = 30;

const formSchema = z.object({
    // Trim first so leading/trailing whitespace (e.g. from autofill) doesn't
    // fail validation, then validate the trimmed value as an email.
    email: z
        .string()
        .trim()
        .pipe(z.email(i18n.t('ResetPassword.InvalidEmail'))),
});

type FormValues = z.infer<typeof formSchema>;

export const ResetPassword: React.FC<PropsWithChildren<unknown>> = () => {
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [cooldown, setCooldown] = useState<number>(0);
    const resetPassword = useMutation(createTokenMutation());

    const form = useForm<FormValues>({
        defaultValues: { email: '' },
        resolver: zodResolver(formSchema),
    });

    // Trimmed to match what the schema submits — the echo and the empty-check
    // should reflect the value the link is actually sent to, not stray whitespace.
    const email = form.watch('email').trim();

    // Tick the cooldown down to zero, one second at a time.
    useEffect(() => {
        if (cooldown <= 0) {
            return undefined;
        }
        const timer = setTimeout(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Bold the echoed address without losing per-language word order: interpolate
    // it, then split the rendered string around it. React escapes each part.
    const confirmation = i18n.t('ResetPasswordResponse.Description', { email });
    const [beforeEmail, afterEmail] = confirmation.split(email);

    async function submit({ email: submittedEmail }: FormValues) {
        try {
            await resetPassword.mutateAsync({ path: { email: submittedEmail } });
            // The backend responds identically (200) whether or not the address is
            // registered, so reaching here reveals nothing about account existence.
            setSubmitted(true);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        } catch {
            // A transport-level failure is not correlated with account existence,
            // so a generic inline error leaks nothing and lets the user retry.
            form.setError('email', { message: i18n.t('Errors.Generic') });
        }
    }

    const getButtonText = (): string => {
        if (cooldown > 0) {
            return i18n.t('ResetPassword.RetryIn', { seconds: cooldown });
        }
        return submitted ? i18n.t('Retry') : i18n.t('ResetPassword.SubmitButton');
    };

    return (
        <AuthLayout
            brand={
                <BrandPanel
                    heading={i18n.t('BrandPanel.Header')}
                    subHeading={i18n.t('BrandPanel.SubHeader')}
                />
            }
        >
            {/* The form stays put and the confirmation appears below the input
                (above the button, so it stays visible on mobile with the
                keyboard up). AuthLayout caps the column width, so the long
                confirmation text wraps within it instead of growing the panel
                and shifting the layout. The still-present input is how a user
                corrects a wrong/typo'd address and retries. */}
            <Group form onSubmit={form.handleSubmit(submit)}>
                <Group>
                    <Heading level={Level.h1}>{i18n.t('ResetPassword.Header')}</Heading>
                    <Text>{i18n.t('ResetPassword.Description')}</Text>
                </Group>
                <Group>
                    <Controller
                        control={form.control}
                        name="email"
                        render={({ field, fieldState }) => (
                            <TextInput
                                label={i18n.t('ResetPassword.EmailLabel')}
                                name={field.name}
                                value={field.value}
                                onChange={(value: string) => {
                                    field.onChange(value);
                                    // Editing invalidates the previous
                                    // confirmation (which echoed the old value)
                                    // and its cooldown — a different address
                                    // isn't spam, so it can be sent right away.
                                    setSubmitted(false);
                                    setCooldown(0);
                                }}
                                onBlur={field.onBlur}
                                message={fieldState.error?.message}
                                informationType={fieldState.error?.message && InformationType.ERROR}
                            />
                        )}
                    />
                </Group>
                {submitted && (
                    <Group>
                        <Heading level={Level.h3}>{i18n.t('ResetPasswordResponse.Header')}</Heading>
                        <Text>
                            {afterEmail === undefined ? (
                                confirmation
                            ) : (
                                <>
                                    {beforeEmail}
                                    <strong>{email}</strong>
                                    {afterEmail}
                                </>
                            )}
                        </Text>
                    </Group>
                )}
                <Group>
                    <Button
                        text={getButtonText()}
                        disabled={!email || cooldown > 0}
                        loading={form.formState.isSubmitting}
                        type="submit"
                        name="submit"
                    />
                </Group>
            </Group>
        </AuthLayout>
    );
};
